import type { FC, PropsWithChildren, ReactNode, Ref } from 'react';
import { useMemo } from 'react';
import type {
  RefreshControlProps,
  ScrollView,
  ScrollViewProps,
  ViewStyle
} from 'react-native';
import { StyleSheet } from 'react-native';
import { ScrollView as GestureScrollView } from 'react-native-gesture-handler';
import type {
  KeyboardAwareScrollViewMode,
  KeyboardAwareScrollViewProps
} from 'react-native-keyboard-controller';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { ActivityIndicator } from 'react-native-paper';
import Animated from 'react-native-reanimated';

import { InfoIcon } from '@/assets';
import { Expand } from '@/shared/components/layout/Expand';
import { FallbackContent } from '@/shared/components/state-view/FallbackContent';
import { FallbackWrapper } from '@/shared/components/state-view/FallbackWrapper';
import { Refresher } from '@/shared/components/state-view/Refresher';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type StateViewScrollComponent = FC<
  ScrollViewProps &
    Partial<
      Pick<
        KeyboardAwareScrollViewProps,
        'bottomOffset' | 'extraKeyboardSpace' | 'mode'
      >
    > & { ref?: Ref<ScrollView | null> }
>;

/**
 * KeyboardAwareScrollView wrapping the gesture-handler ScrollView: keyboard
 * handling + drag/scroll gesture negotiation (e.g. OrganizationEditScreen:
 * form inputs + sortable image grid).
 */
const KeyboardAwareGestureScrollView: StateViewScrollComponent = ({
  ref,
  ...props
}) => (
  <KeyboardAwareScrollView
    ref={ref as never}
    ScrollViewComponent={GestureScrollView as never}
    {...props}
  />
);

const styles = StyleSheet.create({
  view: { flex: 1, flexGrow: 1 },
  stateCentered: { justifyContent: 'center' },
  stateTop: { justifyContent: 'flex-start' }
});

export type StateViewProps = {
  ref?: Ref<ScrollView | null>;
  isLoading?: boolean;
  isRefetching?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  /**
   * Note: not needed if already passed with the viewStyle (will be overwritten by viewStyle.backgroundColor)
   */
  backgroundColor?: ViewStyle['backgroundColor'];
  viewStyle?: ViewStyle;
  state?: {
    loading?: ReactNode;
    error?: ReactNode;
    empty?: ReactNode;
  };
  // Todo: maybe remove and use FallbackContent directly which can be passed as state
  stateLabels?: {
    loading?: {
      title?: string;
      subtitle?: string;
    };
    error?: {
      title?: string;
      subtitle?: string;
    };
    empty?: {
      title?: string;
      subtitle?: string;
    };
  };
  /**
   * Note: not available if asView is set to true
   */
  asView?: boolean;
  centerIndicators?: boolean;
  /**
   * Use the react-native-keyboard-controller KeyboardAwareScrollView instead
   * of the plain RN one. Opt-in for screens with text inputs: keeps the
   * keyboard open while scrolling and scrolls the focused input above the
   * keyboard (iOS otherwise dismisses the keyboard on scroll).
   */
  keyboardAware?: boolean;
  keyboardAwareMode?: KeyboardAwareScrollViewMode;
  keyboardBottomOffset?: number;
  extraKeyboardSpace?: number;
  /**
   * Route the scroll view through react-native-gesture-handler so a hosted
   * gesture-handler drag (e.g. a react-native-sortables grid) negotiates with
   * the scroll gesture instead of being hard-cancelled by the native
   * ScrollView. Independent of `keyboardAware` — combine both on screens
   * with text inputs AND sortable content.
   */
  gestureScroll?: boolean;
  /**
   * Pass a callback if the view should include pull to refresh
   * Note: not available if asView is set to true
   */
  onRefetch?: () => void;
  progressViewOffset?: RefreshControlProps['progressViewOffset'];
  refreshColor?: RefreshControlProps['tintColor'];
} &
  /**
   * Note: will not make any change  if asView is set to true
   * Note: backgroundColor or viewStyle.backgroundColor will be used as backgroundColor of contentContainerStyle
   */
  ScrollViewProps &
  PropsWithChildren;

export const StateView: FC<StateViewProps> = ({
  ref,
  isLoading = false,
  isRefetching = false,
  isError,
  isEmpty,
  backgroundColor,
  viewStyle,
  state,
  stateLabels,
  asView = false,
  centerIndicators = true,
  keyboardAware = false,
  keyboardAwareMode,
  keyboardBottomOffset,
  extraKeyboardSpace,
  gestureScroll = false,
  onRefetch,
  progressViewOffset,
  refreshColor,
  children,
  ...scrollViewProps
}) => {
  const theme = useAppTheme();
  const bg = useMemo(() => {
    if (viewStyle?.backgroundColor) return viewStyle.backgroundColor;
    return backgroundColor ?? theme.colors.surfaceContainerLow;
  }, [
    backgroundColor,
    theme.colors.surfaceContainerLow,
    viewStyle?.backgroundColor
  ]);

  const stateStyle: ViewStyle = useMemo(
    () => ({
      ...(centerIndicators ? styles.stateCentered : styles.stateTop),
      backgroundColor: bg
    }),
    [bg, centerIndicators]
  );

  const refreshControl = useMemo(
    () =>
      onRefetch && (
        <Refresher
          refreshing={isRefetching}
          onRefresh={onRefetch}
          colors={refreshColor ? [refreshColor] : undefined}
          tintColor={refreshColor}
          progressViewOffset={progressViewOffset}
        />
      ),
    [isRefetching, onRefetch, progressViewOffset, refreshColor]
  );

  if (isLoading)
    return (
      <FallbackWrapper
        {...scrollViewProps}
        contentContainerStyle={stateStyle}
        refreshControl={refreshControl}
      >
        {state?.loading ?? (
          <FallbackContent
            style={stateStyle}
            header={<ActivityIndicator size={80} />}
            {...stateLabels?.loading}
          />
        )}
      </FallbackWrapper>
    );

  if (isError)
    return (
      <FallbackWrapper
        {...scrollViewProps}
        contentContainerStyle={stateStyle}
        refreshControl={refreshControl}
      >
        {state?.error ?? (
          <FallbackContent
            style={stateStyle}
            {...stateLabels?.error}
            icon={<InfoIcon height={72} width={72} fill={theme.colors.error} />}
          />
        )}
      </FallbackWrapper>
    );

  if (isEmpty)
    return (
      <FallbackWrapper
        {...scrollViewProps}
        contentContainerStyle={stateStyle}
        refreshControl={refreshControl}
      >
        {state?.empty ?? (
          <FallbackContent
            style={stateStyle}
            {...stateLabels?.empty}
            icon={
              <InfoIcon
                height={72}
                width={72}
                fill={theme.colors.onSurfaceVariant}
              />
            }
          />
        )}
      </FallbackWrapper>
    );

  let ScrollComponent: StateViewScrollComponent = Animated.ScrollView;
  if (keyboardAware && gestureScroll) {
    ScrollComponent = KeyboardAwareGestureScrollView;
  } else if (keyboardAware) {
    ScrollComponent =
      KeyboardAwareScrollView as unknown as StateViewScrollComponent;
  } else if (gestureScroll) {
    ScrollComponent = GestureScrollView as unknown as StateViewScrollComponent;
  }

  return asView ? (
    <Expand style={[viewStyle, { backgroundColor: bg }]}>{children}</Expand>
  ) : (
    <ScrollComponent
      {...scrollViewProps}
      {...(keyboardAware
        ? {
            bottomOffset: keyboardBottomOffset,
            extraKeyboardSpace,
            mode: keyboardAwareMode
          }
        : undefined)}
      ref={ref}
      style={[styles.view, viewStyle, { backgroundColor: bg }]}
      contentContainerStyle={[
        scrollViewProps.contentContainerStyle,
        { backgroundColor: bg }
      ]}
      refreshControl={refreshControl}
    >
      {children}
    </ScrollComponent>
  );
};
